import { default as Modal } from 'antd/es/modal';
import { CountryFlag } from 'shared/src/CountryFlag';
import { CryptoFlag } from 'shared/src/CryptoFlag';
import { CryptoNetworkIcon } from 'shared/src/CryptoNetworkIcon';
import { getCountryCode } from 'shared/src/getCountryCode';

import { capitalizeFirstLetter } from 'utils/capitalize-first-letter';
import { formatHours } from 'utils/dayjs';
import { hasValue } from 'utils/hasValue';

import type { GetCryptoExchanges200DataItem } from 'api/customerApi';

import { FormattedDate } from '@atoms/FormattedDate/FormattedDate';

import { fallbackTradeStatusColor, tradeStatusColor } from './tradeStatusColor';

import { StatusBadge } from '../shared/StatusBadge';
import { shiftDecimal } from '../utils';

type Props = {
  trade: GetCryptoExchanges200DataItem;
  onClose: () => void;
};

export const ExchangeDetailModal = ({ trade, onClose }: Props) => {
  const status = trade.status_histories?.at(-1)?.status;

  return (
    <Modal
      title={null}
      open={!!trade}
      onCancel={onClose}
      footer={null}
      width={400}
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-center gap-4">
          <p className="text-xl">
            {trade.from_currency} → {trade.to_currency}
          </p>

          <div className="flex items-center gap-2">
            <div className="relative inline-flex">
              <CryptoFlag
                cryptoCode={trade.from_currency ?? ''}
                className="size-8"
              />
              <CryptoNetworkIcon
                network={trade.from_network ?? ''}
                className="absolute -bottom-0.5 -right-0.5 size-4"
              />
            </div>

            <span className="text-content-secondary">→</span>
            <CountryFlag
              className="!size-[32px]"
              countryCode={getCountryCode(trade.to_currency ?? '')}
              svg
            />
          </div>

          {trade.created_at ? (
            <div className="flex w-[180px] flex-row justify-between text-base">
              <div className="font-light">
                <FormattedDate
                  className="text-medium"
                  value={trade.created_at}
                />{' '}
              </div>
              {formatHours(trade.created_at, 'cet')}
            </div>
          ) : (
            ''
          )}
        </div>

        <div className="flex flex-col gap-4 px-2">
          <p className="text-base font-bold text-content-primary">
            Transaction details
          </p>

          <div className="flex flex-row justify-between gap-1">
            <span className="font-semibold text-content-secondary">Amount</span>
            <span className="text-content-primary">
              {hasValue(trade.formatted_from_amount)
                ? `-${trade.formatted_from_amount} ${trade.from_currency}`
                : '-'}
            </span>
          </div>

          <div className="flex flex-row justify-between gap-1">
            <span className="font-semibold text-content-secondary">
              Received
            </span>
            <span className="text-content-primary">
              {hasValue(trade.formatted_to_amount)
                ? `${trade.formatted_to_amount} ${trade.to_currency}`
                : '-'}
            </span>
          </div>

          <div className="flex flex-row justify-between gap-1">
            <span className="font-semibold text-content-secondary">
              Exchange rate
            </span>
            <span className="text-content-primary">
              {hasValue(trade.rate) ? shiftDecimal(trade.rate, 8) : '-'}
            </span>
          </div>

          <div className="flex flex-row justify-between gap-1">
            <span className="font-semibold text-content-secondary">
              Network
            </span>
            <span className="inline-flex items-center gap-1 text-sm text-content-primary">
              <CryptoNetworkIcon
                network={trade.from_network ?? ''}
                className="size-4"
              />
              {hasValue(trade.from_network)
                ? capitalizeFirstLetter(trade.from_network.toLowerCase())
                : '-'}
            </span>
          </div>

          <div className="flex flex-row justify-between gap-1">
            <span className="font-semibold text-content-secondary">Status</span>
            <div>
              {hasValue(status) ? (
                <StatusBadge
                  statusText={status}
                  color={tradeStatusColor[status] ?? fallbackTradeStatusColor}
                />
              ) : (
                '-'
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
